export const companyInfo = {
  name: "CISO Dynamics",
  tagline: "Strategic Cybersecurity Advisory for the Enterprise",
  heroHeadline: "Elevating Cyber Resilience Through Strategic Leadership",
  heroDescription: "We partner with executive leadership and boards to navigate complex cybersecurity challenges, strengthen compliance posture, and build enduring security programs.",
  ctaPrimary: "Request a Consultation",
  ctaSecondary: "Explore Our Practice Areas",
  about: "CISO Dynamics is a premier cybersecurity advisory firm delivering executive-level counsel to organizations navigating complex risk, compliance, and security challenges. We bring the strategic depth of top-tier management consulting to the cybersecurity domain, serving as trusted advisors to CISOs, boards, and executive leadership teams across healthcare, financial services, government, and enterprise sectors.",
};

export const stats = [
  { value: "200+", label: "Enterprise Engagements" },
  { value: "15+", label: "Years Advisory Experience" },
  { value: "50+", label: "Frameworks Mastered" },
  { value: "98%", label: "Client Retention" },
];

export const clients = [
  {
    name: "Sentara Health",
    logo: "/logos/sentara.png",
    fallbackLogo: null,
  },
  {
    name: "NOAA",
    logo: "/logos/noaa.png",
    fallbackLogo: null,
  },
  {
    name: "National Weather Service",
    logo: "https://www.weather.gov/images/nws/nws_logo.png",
    fallbackLogo: null,
  },
  {
    name: "U.S. Patent and Trademark Office",
    logo: "/logos/uspto.gif",
    fallbackLogo: null,
  },
  {
    name: "Department of Veterans Affairs",
    logo: "https://www.va.gov/img/design/logo/va-logo.png",
    fallbackLogo: null,
  },
  {
    name: "Department of the Treasury",
    logo: "/logos/treasury.png",
    fallbackLogo: null,
  },
  {
    name: "Internal Revenue Service",
    logo: "/logos/irs.webp",
    fallbackLogo: null,
  },
  {
    name: "Amazon Web Services",
    logo: "https://a0.awsstatic.com/libra-css/images/logos/aws_smile-header-desktop-en-white_59x35.png",
    fallbackLogo: null,
  },
  {
    name: "Dept. of Health & Human Services",
    logo: "/logos/hhs.avif",
    fallbackLogo: null,
  },
  {
    name: "USDA",
    logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/5/5f/Seal_of_the_United_States_Department_of_Agriculture.svg/150px-Seal_of_the_United_States_Department_of_Agriculture.svg.png",
    fallbackLogo: null,
  },
  {
    name: "Holland & Knight",
    logo: "/logos/hollandknight.png",
    fallbackLogo: null,
  },
  {
    name: "xAI",
    logo: "https://x.ai/favicon.ico",
    fallbackLogo: null,
  },
];


export const services = [
  {
    id: "compliance",
    title: "Cyber Compliance & Regulatory Advisory",
    shortDescription: "Navigate complex regulatory landscapes with confidence. We provide end-to-end compliance advisory across enterprise security frameworks.",
    description: "Our compliance practice delivers strategic guidance through the full lifecycle of regulatory adherence, from initial gap assessment through certification achievement and continuous compliance monitoring. We translate regulatory complexity into clear, executable roadmaps aligned with your business objectives.",
    deliverables: [
      "Regulatory gap analysis and roadmap development",
      "Framework implementation and certification support",
      "Continuous compliance monitoring strategies",
      "Audit preparation and evidence management",
      "Policy and procedure development"
    ],
    frameworks: ["NIST", "FedRAMP", "HIPAA", "HITRUST", "SOC 2", "ISO 27001", "CMMC", "IRAP", "PCI-DSS", "MARS-E", "ARC-AMPE", "VITA SEC530", "Zero Trust"],
    icon: "Shield",
    image: "https://images.pexels.com/photos/7108721/pexels-photo-7108721.jpeg?auto=compress&cs=tinysrgb&w=600",
  },
  {
    id: "executive-advisory",
    title: "Executive Cybersecurity Advisory",
    shortDescription: "Strategic counsel for CISOs, boards, and executive leadership. Virtual CISO services, board-level risk communication, and executive decision support.",
    description: "Our executive advisory practice serves as a strategic extension of your leadership team. We bring decades of CISO-level experience to bear on your most critical cybersecurity decisions, providing the clarity and confidence leadership needs to navigate an evolving threat landscape.",
    deliverables: [
      "Virtual CISO (vCISO) engagements",
      "Board-level risk communication and reporting",
      "Executive decision support frameworks",
      "Security posture briefings and assessments",
      "Leadership alignment and stakeholder engagement"
    ],
    frameworks: [],
    icon: "Users",
    image: "https://images.pexels.com/photos/34736064/pexels-photo-34736064.png?auto=compress&cs=tinysrgb&w=600",
  },
  {
    id: "program-development",
    title: "Cybersecurity Program Development",
    shortDescription: "Design and build enterprise-grade security programs. From governance frameworks to operating models, we establish the foundation for lasting security maturity.",
    description: "We design comprehensive cybersecurity programs that align security investments with business outcomes. Our approach integrates governance, risk management, and compliance into a cohesive operating model that scales with your organization.",
    deliverables: [
      "Enterprise security program architecture",
      "Governance, Risk, and Compliance (GRC) frameworks",
      "Security policy and standards development",
      "Operating model design and implementation",
      "Security organization structure and staffing plans"
    ],
    frameworks: [],
    icon: "BarChart3",
    image: "https://images.pexels.com/photos/10530237/pexels-photo-10530237.jpeg?auto=compress&cs=tinysrgb&w=600",
  },
  {
    id: "tool-development",
    title: "Cyber Tool & Platform Development",
    shortDescription: "Purpose-built security tools and platforms. Custom dashboards, GRC platforms, and risk analytics solutions tailored to your operational needs.",
    description: "Our engineering practice delivers custom cybersecurity tooling that closes the gaps left by commercial products. From executive dashboards that distill complex risk data into actionable intelligence to full-scale GRC platforms, we build solutions that fit your unique requirements.",
    deliverables: [
      "Security executive dashboards",
      "Custom GRC platform development",
      "Risk and vulnerability analytics tools",
      "Automated compliance reporting solutions",
      "Integration with existing security ecosystems"
    ],
    frameworks: [],
    icon: "Code",
    image: "https://images.pexels.com/photos/577210/pexels-photo-577210.jpeg?auto=compress&cs=tinysrgb&w=600",
  },
  {
    id: "vulnerability-management",
    title: "Vulnerability Management & Risk Operations",
    shortDescription: "End-to-end vulnerability lifecycle management. Identification, prioritization, remediation tracking, and risk reporting.",
    description: "Our vulnerability management practice transforms reactive patching into a strategic risk reduction program. We implement disciplined processes for the full vulnerability lifecycle, ensuring that remediation efforts are prioritized by actual business risk and tracked to completion.",
    deliverables: [
      "Vulnerability lifecycle management programs",
      "Remediation tracking and CAPA processes",
      "Risk-based prioritization frameworks",
      "Metrics and KPI development",
      "Vulnerability management tool optimization"
    ],
    frameworks: [],
    icon: "Bug",
    image: "https://images.pexels.com/photos/2881232/pexels-photo-2881232.jpeg?auto=compress&cs=tinysrgb&w=600",
  },
  {
    id: "strategy-readiness",
    title: "Cybersecurity Strategy & Readiness",
    shortDescription: "Comprehensive maturity assessments and strategic planning. Evaluate your security posture, identify gaps, and develop actionable plans for cyber resilience.",
    description: "Our strategy practice provides the analytical foundation for informed cybersecurity investment and planning. Through rigorous assessment methodologies, we deliver a clear picture of your current security maturity and a prioritized roadmap to your desired state.",
    deliverables: [
      "Security maturity assessments",
      "Readiness and gap analysis",
      "Cyber resiliency planning",
      "Strategic investment roadmaps",
      "Benchmarking against industry standards"
    ],
    frameworks: [],
    icon: "Target",
    image: "https://images.pexels.com/photos/7688106/pexels-photo-7688106.jpeg?auto=compress&cs=tinysrgb&w=600",
  },
  {
    id: "staffing",
    title: "Cybersecurity Professional Staffing",
    shortDescription: "Vetted cybersecurity professionals for critical roles. Interim CISOs, security architects, GRC analysts, and program managers who deliver immediate impact.",
    description: "Our staffing practice provides pre-vetted cybersecurity professionals who bring immediate value to your organization. Whether you need an interim CISO to lead a transformation, security architects to design your next-generation infrastructure, or GRC analysts to support compliance efforts, we deliver talent that meets the highest professional standards.",
    deliverables: [
      "Interim CISO placements",
      "Security architect staffing",
      "GRC analyst resources",
      "Program and project manager placement",
      "Team augmentation and capacity planning"
    ],
    frameworks: [],
    icon: "Briefcase",
    image: "https://images.pexels.com/photos/8102000/pexels-photo-8102000.jpeg?auto=compress&cs=tinysrgb&w=600",
  },
  {
    id: "third-party-risk",
    title: "Third Party Risk Management",
    shortDescription: "Comprehensive vendor and supply chain risk programs. We assess, monitor, and manage cybersecurity risk across your third-party ecosystem to protect your extended enterprise.",
    description: "Our third party risk management practice helps organizations build and mature programs that identify, assess, and mitigate cybersecurity risk introduced through vendors, suppliers, and business partners. As organizations increasingly depend on external ecosystems, the attack surface extends well beyond the enterprise boundary. We deliver structured approaches to vendor risk assessment, continuous monitoring, and contractual risk governance that protect your organization without impeding business velocity.",
    deliverables: [
      "Third-party risk program design and maturity",
      "Vendor security assessment frameworks",
      "Continuous monitoring and risk scoring",
      "Supply chain risk analysis",
      "Contractual security requirements and SLA development"
    ],
    frameworks: [],
    icon: "Network",
    image: "https://images.pexels.com/photos/2881232/pexels-photo-2881232.jpeg?auto=compress&cs=tinysrgb&w=600",
  },
  {
    id: "executive-cyber-training",
    title: "Executive Cyber Training for Leadership",
    shortDescription: "Tailored cybersecurity education for executives and board members. We equip leadership with the knowledge to make informed risk decisions and fulfill their governance responsibilities.",
    description: "Our executive cyber training practice delivers structured, role-appropriate cybersecurity education designed specifically for C-suite executives, board directors, and senior leadership teams. Unlike technical training programs, our curriculum is built around business risk, governance obligations, and strategic decision-making, ensuring leaders can engage meaningfully with cybersecurity as a business imperative rather than a technical domain they cannot access.",
    deliverables: [
      "Board and C-suite cybersecurity awareness programs",
      "Tabletop exercises and incident simulation for executives",
      "Cyber risk literacy workshops",
      "Governance and fiduciary duty training",
      "Custom leadership briefing development"
    ],
    frameworks: [],
    icon: "GraduationCap",
    image: "https://images.pexels.com/photos/5324985/pexels-photo-5324985.jpeg?auto=compress&cs=tinysrgb&w=600",
  },
  {
    id: "ai-governance-risk",
    title: "AI Governance & Risk Management",
    shortDescription: "Strategic advisory for governing AI systems and managing emerging risks. We help organizations establish responsible AI frameworks that address security, privacy, bias, and regulatory compliance.",
    description: "As organizations accelerate AI adoption, the governance and risk landscape grows increasingly complex. Our AI governance practice helps leadership teams establish the policies, controls, and oversight structures needed to deploy AI responsibly. We address the intersection of AI security, data privacy, algorithmic bias, model integrity, and evolving regulatory requirements, ensuring your AI initiatives create value without introducing unacceptable risk.",
    deliverables: [
      "AI governance framework development",
      "AI risk assessment and mitigation strategies",
      "Responsible AI policy and standards creation",
      "AI security and adversarial risk evaluation",
      "Regulatory compliance for AI systems (EU AI Act, NIST AI RMF)"
    ],
    frameworks: ["NIST AI RMF", "EU AI Act", "ISO 42001"],
    icon: "Brain",
    image: "https://images.pexels.com/photos/8386437/pexels-photo-8386437.jpeg?auto=compress&cs=tinysrgb&w=600",
  },
];

export const approach = [
  {
    step: 1,
    title: "Strategy",
    description: "Deep discovery understanding your business context, risk landscape, regulatory obligations, and strategic objectives to define a clear security vision.",
  },
  {
    step: 2,
    title: "Design",
    description: "Architecting solutions tailored to your organization designing programs, frameworks, and capabilities that align security investments with measurable outcomes.",
  },
  {
    step: 3,
    title: "Implement",
    description: "Executing with precision deploying capabilities, establishing processes, and embedding security practices into your operational fabric with minimal disruption.",
  },
  {
    step: 4,
    title: "Operate",
    description: "Ensuring sustained performance providing ongoing advisory, monitoring effectiveness, and evolving your security posture as threats and needs change.",
  },
];

export const industries = [
  {
    id: "healthcare",
    title: "Healthcare",
    description: "Protecting patient data and clinical systems while navigating HIPAA, HITRUST, and evolving healthcare-specific regulations. We understand the balance between security, compliance, and patient care continuity.",
    challenges: ["HIPAA & HITRUST compliance", "PHI protection", "Medical device security", "Clinical workflow continuity"],
    image: "https://images.pexels.com/photos/31499386/pexels-photo-31499386.jpeg?auto=compress&cs=tinysrgb&w=600",
  },
  {
    id: "financial-services",
    title: "Financial Services",
    description: "Securing financial infrastructure and customer data across banking, insurance, and capital markets. Our advisory addresses the intersection of regulatory compliance, operational resilience, and digital transformation.",
    challenges: ["SOC 2 & regulatory compliance", "Financial data protection", "Third-party risk management", "Digital transformation security"],
    image: "https://images.unsplash.com/photo-1511883040705-6011fad9edfc?auto=compress&cs=tinysrgb&w=600",
  },
  {
    id: "government",
    title: "Government & Public Sector",
    description: "Supporting federal, state, and local agencies with FedRAMP, CMMC, and NIST compliance. We bring deep understanding of government security requirements and procurement processes.",
    challenges: ["FedRAMP & CMMC compliance", "NIST framework implementation", "Controlled unclassified information", "Zero trust architecture"],
    image: "https://images.pexels.com/photos/30256874/pexels-photo-30256874.jpeg?auto=compress&cs=tinysrgb&w=600",
  },
  {
    id: "enterprise",
    title: "Enterprise & Regulated Industries",
    description: "Serving large organizations across manufacturing, technology, and other regulated sectors. We deliver security strategies that scale with enterprise complexity and support business growth.",
    challenges: ["Enterprise-scale security programs", "Multi-framework compliance", "Global security operations", "M&A security integration"],
    image: "https://images.pexels.com/photos/16370914/pexels-photo-16370914.jpeg?auto=compress&cs=tinysrgb&w=600",
  },
  {
    id: "oil-gas",
    title: "Oil & Gas",
    description: "Protecting upstream, midstream, and downstream operations from cyber threats targeting industrial control systems, SCADA networks, and critical production infrastructure across global supply chains.",
    challenges: ["ICS/SCADA security", "Pipeline infrastructure protection", "Remote site connectivity", "Supply chain risk management"],
    image: "https://images.unsplash.com/photo-1516937941344-00b4e0337589?auto=compress&cs=tinysrgb&w=600",
  },
  {
    id: "energy",
    title: "Energy",
    description: "Securing power generation, transmission, and distribution systems while meeting NERC CIP compliance requirements and defending against nation-state threats targeting critical energy infrastructure.",
    challenges: ["NERC CIP compliance", "Grid modernization security", "OT/IT convergence", "Critical infrastructure protection"],
    image: "https://images.pexels.com/photos/12157172/pexels-photo-12157172.jpeg?auto=compress&cs=tinysrgb&w=600",
  },
  {
    id: "automotive",
    title: "Automotive",
    description: "Addressing cybersecurity across connected vehicles, manufacturing systems, and the evolving automotive supply chain, including compliance with emerging vehicle cybersecurity regulations and standards.",
    challenges: ["Connected vehicle security", "Manufacturing OT protection", "Supply chain integrity", "ISO/SAE 21434 compliance"],
    image: "https://images.unsplash.com/photo-1567789884554-0b844b597180?auto=compress&cs=tinysrgb&w=600",
  },
  {
    id: "construction",
    title: "Construction",
    description: "Safeguarding project data, building management systems, and increasingly connected construction sites from cyber risks while protecting intellectual property and sensitive bidding information.",
    challenges: ["Project data protection", "Building management systems", "IoT device security", "Subcontractor risk management"],
    image: "https://images.unsplash.com/photo-1535732759880-bbd5c7265e3f?auto=compress&cs=tinysrgb&w=600",
  },
  {
    id: "aviation",
    title: "Aviation",
    description: "Securing aviation operations, air traffic management systems, and passenger data across airlines, airports, and aerospace manufacturers within a highly regulated global framework.",
    challenges: ["Aviation systems security", "Passenger data protection", "TSA cybersecurity directives", "Aircraft systems integrity"],
    image: "https://images.unsplash.com/photo-1596910891038-c79cb84fae3a?auto=compress&cs=tinysrgb&w=600",
  },
  {
    id: "marine",
    title: "Marine",
    description: "Protecting maritime operations, port infrastructure, and vessel navigation systems from cyber threats while meeting IMO maritime cybersecurity guidelines and coast guard requirements.",
    challenges: ["Vessel OT security", "Port infrastructure protection", "IMO compliance", "Navigation system integrity"],
    image: "https://images.unsplash.com/photo-1704998618751-aea8bf0f3b2b?auto=compress&cs=tinysrgb&w=600",
  },
  {
    id: "non-profit",
    title: "Non-Profit",
    description: "Delivering right-sized cybersecurity programs for mission-driven organizations, protecting donor data, and ensuring compliance with grant requirements and data stewardship obligations.",
    challenges: ["Donor data protection", "Grant compliance requirements", "Budget-conscious security", "Volunteer access management"],
    image: "https://images.unsplash.com/photo-1778864875228-caa80c73cbd3?auto=compress&cs=tinysrgb&w=600",
  },
  {
    id: "agriculture",
    title: "Agriculture",
    description: "Securing precision agriculture systems, smart farming infrastructure, and agricultural supply chains against evolving cyber risks targeting food production and distribution networks.",
    challenges: ["Precision agriculture systems", "Smart farming IoT security", "Supply chain integrity", "Food safety data protection"],
    image: "https://images.unsplash.com/photo-1560493676-04071c5f467b?auto=compress&cs=tinysrgb&w=600",
  },
  {
    id: "logistics",
    title: "Logistics",
    description: "Protecting global logistics networks, warehouse management systems, and real-time tracking infrastructure from disruptions that can cascade across interconnected supply chains.",
    challenges: ["Supply chain visibility security", "Warehouse automation protection", "Fleet management systems", "Third-party integration risk"],
    image: "https://images.unsplash.com/photo-1553413077-190dd305871c?auto=compress&cs=tinysrgb&w=600",
  },
  {
    id: "telecommunication",
    title: "Telecommunication",
    description: "Securing telecommunications infrastructure, 5G networks, and customer data across carriers and service providers operating under stringent regulatory and national security obligations.",
    challenges: ["5G infrastructure security", "Network function virtualization", "Customer data protection", "Regulatory compliance"],
    image: "https://images.unsplash.com/photo-1643155193188-38eb08e2b54f?auto=compress&cs=tinysrgb&w=600",
  },
  {
    id: "real-estate",
    title: "Real Estate",
    description: "Protecting commercial and residential real estate operations, smart building systems, and high-value transaction data across property management, development, and investment portfolios.",
    challenges: ["Smart building security", "Transaction data protection", "Tenant privacy compliance", "Property management systems"],
    image: "https://images.pexels.com/photos/4534504/pexels-photo-4534504.jpeg?auto=compress&cs=tinysrgb&w=600",
  },
  {
    id: "defense",
    title: "Defense",
    description: "Supporting defense contractors, primes, and subcontractors with CMMC certification, ITAR compliance, and the protection of controlled unclassified information across classified and unclassified environments.",
    challenges: ["CMMC certification", "ITAR & export controls", "CUI protection", "Classified environment security"],
    image: "https://images.unsplash.com/photo-1630161861535-b39e5635da68?auto=compress&cs=tinysrgb&w=600",
  },
];

export const leadership = [
  {
    id: 1,
    name: "Alexandra Mercer",
    title: "Managing Partner & Chief Advisory Officer",
    bio: "Over 20 years advising Fortune 500 organizations on cybersecurity strategy. Previously served as CISO at a leading financial institution and senior consultant at a Big Four firm.",
    initials: "AM",
  },
  {
    id: 2,
    name: "David Chen",
    title: "Partner, Compliance & Regulatory Practice",
    bio: "18 years of regulatory and compliance expertise across healthcare, financial services, and government. Led over 100 compliance transformation engagements.",
    initials: "DC",
  },
  {
    id: 3,
    name: "Sarah Okonkwo",
    title: "Partner, Security Program Development",
    bio: "15 years building and maturing enterprise security organizations. Specializes in governance, risk management, and scalable security operating models.",
    initials: "SO",
  },
  {
    id: 4,
    name: "Michael Torres",
    title: "Partner, Technology & Platform Solutions",
    bio: "16 years in security engineering and platform development. Designed and built custom security solutions for organizations across all major industry verticals.",
    initials: "MT",
  },
];

export const insights = [
  {
    id: 1,
    title: "The Board's Role in Cyber Risk Governance: A Framework for Effective Oversight",
    category: "Governance",
    date: "August 2025",
    readTime: "8 min read",
    excerpt: "As regulatory expectations for board-level cybersecurity oversight intensify, organizations need a structured approach to integrating cyber risk into enterprise governance.",
    content: [
      "The governance landscape for cybersecurity has shifted fundamentally. Regulatory bodies, investors, and stakeholders now expect boards to demonstrate meaningful engagement with cyber risk, not merely receive periodic briefings, but actively oversee the organization's security posture as a strategic business concern. The SEC's cybersecurity disclosure rules, evolving NIST guidance, and sector-specific mandates have made board-level oversight a compliance imperative as much as a governance best practice.",
      "Effective board oversight requires a structured framework that translates technical complexity into decision-relevant intelligence. This begins with establishing clear reporting cadences, defining risk appetite in business terms that directors can evaluate, and creating escalation protocols that ensure the board is engaged at the right moments and not overwhelmed with operational detail. The most effective governance models we have observed treat cybersecurity as a standing agenda item with the same rigor applied to financial risk or audit oversight.",
      "Organizations that institutionalize this framework see measurable improvements in their security posture. Board engagement drives resource allocation, strategic alignment, and accountability. It also sends a clear signal throughout the organization that cybersecurity is a leadership priority, not merely an IT function. The result is a governance model where risk-informed decisions are made at the right level, with the right information, at the right time."
    ],
  },
  {
    id: 2,
    title: "Beyond Compliance: Building Security Programs That Drive Business Value",
    category: "Strategy",
    date: "June 2025",
    readTime: "12 min read",
    excerpt: "Compliance is a baseline, not a strategy. Organizations that align security investments with business outcomes achieve measurably better risk reduction and stakeholder confidence.",
    content: [
      "Too many organizations conflate compliance with security. They invest heavily in meeting regulatory requirements, achieving SOC 2 certification, passing HIPAA audits, and maintaining FedRAMP authorization, then assume the resulting controls constitute a security program. While compliance frameworks provide valuable structure and accountability, they represent a minimum threshold, not a strategic objective. The organizations that achieve true security resilience are those that build programs designed to drive business value beyond the audit checklist.",
      "A value-driven security program begins with understanding the business. What are the organization's most critical assets? What does the threat landscape look like for your specific industry and operational profile? Where does security investment yield the greatest risk reduction per dollar spent? These questions require security leaders to operate as business strategists, not technologists. The answers inform a program architecture that aligns security capabilities with business objectives, protecting revenue-generating systems, enabling safe digital transformation, and building the trust that customers and partners expect.",
      "The financial case is compelling. Organizations with strategically aligned security programs report faster incident response times, lower breach costs, and stronger stakeholder confidence. They also find that security becomes a competitive differentiator rather than a cost center, enabling business initiatives that risk-averse competitors cannot pursue. The shift from compliance-driven to value-driven security is not a luxury; for organizations operating in competitive, regulated markets, it is a strategic necessity."
    ],
  },
  {
    id: 3,
    title: "Navigating the 2025 Regulatory Landscape: What CISOs Need to Know",
    category: "Compliance",
    date: "July 2025",
    readTime: "10 min read",
    excerpt: "With significant regulatory changes across healthcare, financial services, and government, CISOs face an increasingly complex compliance environment.",
    content: [
      "The regulatory environment for cybersecurity has never been more complex or consequential. In 2025 alone, CISOs are contending with updated HIPAA Security Rule requirements, expanded SEC disclosure mandates, the maturation of CMMC enforcement for defense contractors, and a growing patchwork of state-level privacy and breach notification laws. Each of these developments carries operational implications: new controls to implement, new reporting obligations to satisfy, and new audit expectations to prepare for.",
      "For CISOs operating across multiple regulatory domains, the challenge is not merely keeping pace with individual requirements but managing the intersections between them. A healthcare organization processing financial transactions must satisfy both HIPAA and PCI-DSS. A government contractor handling controlled unclassified information must navigate CMMC while potentially also addressing FedRAMP requirements. The most effective compliance strategies recognize these overlaps and build unified control frameworks that satisfy multiple regulatory obligations through shared capabilities.",
      "Strategic CISOs are approaching this complexity with a risk-based mindset rather than a checklist mentality. They are investing in compliance architectures that are modular and extensible, designed to absorb new requirements without requiring wholesale program redesign. They are also investing in automation and continuous monitoring capabilities that reduce the manual burden of evidence collection and audit preparation, freeing security teams to focus on genuine risk reduction rather than documentation exercises."
    ],
  },
  {
    id: 4,
    title: "The Virtual CISO Model: When and How to Leverage External Security Leadership",
    category: "Advisory",
    date: "June 2025",
    readTime: "7 min read",
    excerpt: "The Virtual CISO model offers organizations access to senior security leadership without the overhead of a full-time executive hire.",
    content: [
      "Not every organization needs, or can attract, a full-time Chief Information Security Officer. Mid-market companies, high-growth startups, and organizations undergoing leadership transitions often face a critical gap: they need senior security leadership to guide strategy, manage risk, and interface with the board, but the economics or timing of a permanent hire do not align. The Virtual CISO model addresses this gap by providing experienced security executives on a fractional or retained basis, delivering the strategic value of a CISO without the commitment of a full-time role.",
      "The most effective vCISO engagements go beyond advisory into genuine operational leadership. A strong vCISO establishes governance structures, builds relationships with business stakeholders, develops security strategy aligned with organizational objectives, and creates the conditions for a future full-time hire to succeed. They bring pattern recognition from serving multiple organizations, insights that a single-company CISO may take years to develop. This breadth of experience is particularly valuable for organizations navigating complex compliance requirements or building security programs from the ground up.",
      "The key to a successful vCISO engagement is clarity of scope and authority. Organizations must define the role's decision-making authority, reporting relationships, and success metrics before the engagement begins. The vCISO must be integrated into leadership discussions, granted access to the information needed to make informed decisions, and empowered to drive change. When these conditions are met, the model delivers exceptional value, often at a fraction of the cost of a full-time executive, with measurably faster time to impact."
    ],
  },
  {
    id: 5,
    title: "Vulnerability Management Maturity: From Reactive Patching to Risk-Based Operations",
    category: "Operations",
    date: "June 2025",
    readTime: "9 min read",
    excerpt: "Most vulnerability management programs remain stuck in reactive mode. This paper outlines a maturity model for evolving vulnerability operations into a strategic capability.",
    content: [
      "Vulnerability management is one of the most fundamental capabilities in any security program, yet it remains one of the most poorly executed. The typical pattern is familiar: a scanning tool generates thousands of findings, the security team triages what it can, IT operations patches what it has capacity for, and the residual risk is accepted by default rather than by informed decision. The result is a perpetual backlog of unaddressed vulnerabilities, with no clear line of sight into which ones represent genuine business risk and which are noise.",
      "Mature vulnerability management programs break this cycle by shifting from volume-based patching to risk-based prioritization. This requires integrating vulnerability data with asset criticality, threat intelligence, and exploitability context to produce a prioritized remediation queue that reflects actual business risk. It also requires establishing clear remediation SLAs, tracking completion through CAPA-style processes, and reporting metrics that leadership can use to assess program effectiveness. The goal is not to patch everything; it is to ensure that the vulnerabilities most likely to be exploited against your most critical assets are addressed first.",
      "The organizational dimension is equally important. Effective vulnerability management requires collaboration between security, IT operations, development teams, and business stakeholders. It requires executive sponsorship to enforce remediation timelines and resolve resource conflicts. And it requires a continuous improvement mindset, regularly assessing program performance, refining prioritization criteria, and evolving processes as the threat landscape and organizational attack surface change. Organizations that make this investment see measurable reductions in exploitable exposure and meaningful improvements in their overall security posture."
    ],
  },
];

export const contactReasons = [
  "Compliance & Regulatory Advisory",
  "Executive Advisory / vCISO",
  "Security Program Development",
  "Technology & Platform Solutions",
  "Vulnerability Management",
  "Strategy & Readiness Assessment",
  "Third Party Risk Management",
  "Executive Cyber Training",
  "AI Governance & Risk Management",
  "Professional Staffing",
  "General Inquiry",
];

export const navLinks = [
  { label: "Practice Areas", path: "/services" },
  { label: "Approach", path: "/approach" },
  { label: "Industries", path: "/industries" },
  { label: "Insights", path: "/insights" },
];
